import React, { useState } from 'react';
import { X } from 'lucide-react';
import { BulkOperationResultsResponse, Driver, FleetSubtype } from '../../services/api';
import DriverViewModal from './DriverViewModal';

interface BulkCheckResultsModalProps {
    isOpen: boolean;
    onClose: () => void;
    results: BulkOperationResultsResponse | null;
    subtype?: FleetSubtype;
}

const BulkCheckResultsModal: React.FC<BulkCheckResultsModalProps> = ({ isOpen, onClose, results, subtype }) => {
    const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
    const [showDriverModal, setShowDriverModal] = useState(false);

    if (!isOpen || !results) return null;

    const { operation, items } = results.data;

    const bar = (cls: string, label: string) => (
        <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700">
            <span className={`h-4 w-[3px] rounded-full ${cls}`} aria-hidden="true" />
            {label}
        </span>
    );

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'completed':  return bar('bg-green-500', 'Completed');
            case 'failed':     return bar('bg-red-500', 'Failed');
            case 'processing': return bar('bg-blue-500', 'Processing');
            default:           return bar('bg-gray-300', status);
        }
    };

    const getRiskBadge = (risk?: string) => {
        if (!risk) return <span className="text-gray-400 text-xs">-</span>;
        switch (risk) {
            case 'high':   return bar('bg-red-500', 'High');
            case 'medium': return bar('bg-amber-400', 'Medium');
            case 'low':    return bar('bg-green-500', 'Low');
            default:       return bar('bg-gray-300', risk);
        }
    };

    const getLicenceValidity = (item: any) => {
        if (item.resultData?.valid === true)  return bar('bg-green-500', 'Valid');
        if (item.resultData?.valid === false)  return bar('bg-red-500', 'Invalid');
        if (item.status === 'failed')          return bar('bg-gray-300', 'Check Failed');
        return <span className="text-gray-400 text-xs">-</span>;
    };

    const handleViewDriver = (driver: Driver) => {
        setSelectedDriver(driver);
        setShowDriverModal(true);
    };

    return (
        <>
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="flex justify-between items-start p-6 border-b border-gray-200">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Bulk Licence Check Results</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                Operation completed on {operation.completedAt ? new Date(operation.completedAt).toLocaleString('en-GB') : 'N/A'}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Summary Stats */}
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="text-center">
                                <p className="text-2xl font-bold text-gray-900">{operation.totalItems}</p>
                                <p className="text-sm text-gray-600">Total Drivers</p>
                            </div>
                            <div className="text-center">
                                <p className="text-2xl font-bold text-green-600">{operation.successCount}</p>
                                <p className="text-sm text-gray-600">Successful</p>
                            </div>
                            <div className="text-center">
                                <p className="text-2xl font-bold text-red-600">{operation.failureCount}</p>
                                <p className="text-sm text-gray-600">Failed</p>
                            </div>
                        </div>
                    </div>

                    {/* Results Table */}
                    <div className="flex-1 overflow-auto p-6">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-100">
                                <thead className="sticky top-0 z-10 border-b border-gray-100 bg-gray-50/95 backdrop-blur-sm">
                                    <tr>
                                        <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                            Driver
                                        </th>
                                        <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                            Licence Number
                                        </th>
                                        <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                            Validity
                                        </th>
                                        <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                            Risk Level
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                    {items.map((item) => (
                                        <tr key={item.id} className="hover:bg-gray-50/60 transition-colors">
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {item.driver ? `${item.driver.firstName} ${item.driver.lastName}` : 'Unknown'}
                                                </div>
                                                {item.driver?.email && (
                                                    <div className="text-sm text-gray-500">{item.driver.email}</div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                <div className="text-sm font-mono text-gray-900">
                                                    {item.driver?.licenceNumber || '-'}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                {getStatusBadge(item.status)}
                                            </td>
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                {getLicenceValidity(item)}
                                            </td>
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                {getRiskBadge(item.driver?.riskLevel)}
                                            </td>
                                            <td className="px-4 py-3.5 whitespace-nowrap text-right text-sm font-medium">
                                                {item.driver && (
                                                    <button
                                                        onClick={() => handleViewDriver(item.driver!)}
                                                        className="px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors"
                                                        style={{ backgroundColor: 'rgba(105,190,40,0.12)', color: '#3d7a12' }}
                                                    >
                                                        View
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {items.length === 0 && (
                                <div className="text-center py-12">
                                    <p className="text-gray-500">No results found</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2"
                            style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>

            {/* Driver View Modal */}
            {selectedDriver && (
                <DriverViewModal
                    isOpen={showDriverModal}
                    onClose={() => {
                        setShowDriverModal(false);
                        setSelectedDriver(null);
                    }}
                    driver={selectedDriver}
                    subtype={subtype}
                />
            )}
        </>
    );
};

export default BulkCheckResultsModal;
